import { useState } from 'react';
import type { CreateAdminTemplateInput } from '../../lib/adminTemplates';
import { useDialog } from '../../lib/useDialog';

interface NewTemplateDialogProps {
  onClose: () => void;
  onSubmit: (input: CreateAdminTemplateInput) => void;
  pending: boolean;
}

/** Header action on `/admin/templates`: key/name/series label → a blank draft (version 1 of a new
 * key). Exact key/name/label length and charset rules are enforced server-side (400
 * `validation_failed`) and surfaced as a toast by the caller — this dialog only gates the submit
 * button on "non-blank", matching `NewRoomDialog`'s division of labor. */
export function NewTemplateDialog({ onClose, onSubmit, pending }: NewTemplateDialogProps) {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [seriesLabel, setSeriesLabel] = useState('');
  const { dialogRef, onBackdropClick } = useDialog(onClose);

  const canSubmit = key.trim() !== '' && name.trim() !== '' && seriesLabel.trim() !== '';

  function submit() {
    if (!canSubmit) {
      return;
    }
    onSubmit({ key: key.trim(), name: name.trim(), seriesLabel: seriesLabel.trim() });
  }

  return (
    <div
      ref={dialogRef}
      onClick={onBackdropClick}
      className="fixed inset-0 z-50 flex items-end justify-center bg-bg/80 p-4 md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label="New template"
      tabIndex={-1}
    >
      <div className="w-full max-w-lg rounded-lg border border-border-strong bg-bg-elevated p-6">
        <div className="flex items-start justify-between gap-4">
          <h2 className="display text-3xl">New template</h2>
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap min-w-tap rounded-md border border-border-strong px-3 text-sm text-text-muted hover:border-accent-border hover:text-text"
          >
            Close
          </button>
        </div>

        <div className="mt-5 flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="flex flex-col gap-1 text-sm text-text-muted">
              <span className="eyebrow">Key</span>
              <input
                type="text"
                value={key}
                onChange={(event) => setKey(event.target.value)}
                placeholder="season-3"
                className="telemetry min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
              />
            </label>
            <span className="telemetry text-text-faint">Lowercase, digits, hyphens. Immutable once created.</span>
          </div>

          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Name</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Season 3 Class Interviews"
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
            />
          </label>

          <label className="flex flex-col gap-1 text-sm text-text-muted">
            <span className="eyebrow">Series label</span>
            <input
              type="text"
              value={seriesLabel}
              onChange={(event) => setSeriesLabel(event.target.value)}
              placeholder="Season 3"
              className="min-h-tap rounded-md border border-border-strong bg-bg px-3 text-text"
            />
          </label>
        </div>

        <button
          type="button"
          disabled={!canSubmit || pending}
          onClick={submit}
          className="mt-6 min-h-tap w-full rounded-md border border-accent-border bg-accent-dim px-4 text-base font-semibold text-accent disabled:cursor-not-allowed disabled:opacity-50"
        >
          Create draft
        </button>
      </div>
    </div>
  );
}
